import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'gatsby';
import { optimizeImageUrl } from '../lib/prismicImage';

/* ─── Sub-elements (defined before parents for selector references) ─── */

const BgImage = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  transition: transform 0.4s ease;
`;

const Gradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(0,0,0,0.75) 0%,
    rgba(0,0,0,0.3) 50%,
    rgba(0,0,0,0.1) 100%
  );
`;

const SlideTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.3;
  margin: 0 0 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  @media (min-width: 768px) { font-size: 2rem; }
  @media (min-width: 1024px) { font-size: 2.25rem; }
`;

/* ─── Container ─── */

const CarouselInner = styled.div`
  position: relative;
  height: 100%;
  min-height: 300px;
  cursor: pointer;
  overflow: hidden;
  border-radius: 0.5rem;
  @media (min-width: 768px) { min-height: 400px; }
  &:hover ${BgImage} { transform: scale(1.03); }
`;

const CarouselWrapper = styled.div`
  position: relative;
  height: 100%;
  min-height: 300px;
  @media (min-width: 768px) { min-height: 400px; }
`;

const ContentOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem;
  color: white;
  z-index: 10;
  @media (min-width: 768px) { padding: 2rem; }
`;

const CategoryBadge = styled.span`
  display: inline-block;
  background: #dc2626;
  color: white;
  padding: 0.2rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  border-radius: 0.25rem;
  margin-bottom: 0.5rem;
`;

const Indicators = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const IndicatorBtn = styled.button`
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.active ? 'rgba(255,255,255,0.3)' : 'transparent'};
  border: none;
  cursor: pointer;
  border-radius: 9999px;
  transition: background 0.2s;
  &:hover { background: rgba(255,255,255,0.15); }
`;

const IndicatorDot = styled.span`
  display: block;
  height: 0.5rem;
  border-radius: 9999px;
  background: ${props => props.active ? 'white' : 'rgba(255,255,255,0.35)'};
  width: ${props => props.active ? '2rem' : '1rem'};
  transition: width 0.25s ease, background 0.25s ease;
`;

const EmptyState = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--border);
  padding: 4rem 2rem;
  text-align: center;
  height: 100%;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  @media (min-width: 768px) { min-height: 400px; }
`;

/* ─── Component ─── */

const FeaturedCarousel = ({ articles }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  useEffect(() => {
    if (!autoPlay || !articles || articles.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev === articles.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [currentIndex, autoPlay, articles]);

  const goToSlide = (index) => {
    setAutoPlay(false);
    setCurrentIndex(index);
  };

  if (!articles || articles.length === 0) {
    return (
      <EmptyState>
        <h2 style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-secondary)', margin: '0 0 0.75rem' }}>
          FEATURED NEWS
        </h2>
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>No featured articles available</p>
      </EmptyState>
    );
  }

  const article = articles[currentIndex];
  if (!article || !article.node) return null;

  const { node } = article;
  const title = node.data.title ? node.data.title.text : '';
  const imgUrl = node.data.img ? node.data.img.url : '';
  const uid = node.uid || '';
  const thirdPartyUrl = node.data.third_party_url || '';

  let href = `/${uid}/`;
  if (thirdPartyUrl) {
    href = thirdPartyUrl.startsWith('http') ? thirdPartyUrl : `https://${thirdPartyUrl}`;
  }

  const isExternal = !!thirdPartyUrl;

  const slideContent = (
    <CarouselInner>
      {imgUrl && <BgImage src={optimizeImageUrl(imgUrl, 1200, 80)} />}
      <Gradient />
      <ContentOverlay>
        <SlideTitle>{title}</SlideTitle>
        <Indicators>
          {articles.map((_, index) => (
            <IndicatorBtn
              key={index}
              active={index === currentIndex}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); goToSlide(index); }}
              aria-label={`Go to slide ${index + 1}`}
            >
              <IndicatorDot active={index === currentIndex} />
            </IndicatorBtn>
          ))}
        </Indicators>
      </ContentOverlay>
    </CarouselInner>
  );

  return (
    <CarouselWrapper>
      {isExternal ? (
        <a href={href} target="_blank" rel="noopener noreferrer" style={{ display: 'block', height: '100%', textDecoration: 'none' }}>
          {slideContent}
        </a>
      ) : (
        <Link to={href} style={{ display: 'block', height: '100%', textDecoration: 'none' }}>
          {slideContent}
        </Link>
      )}
    </CarouselWrapper>
  );
};

export default FeaturedCarousel;
